import React, { useState } from 'react'
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, Typography } from '@mui/material'
import DisabledReason from '../system/DisabledReason'
import GuideTracker, { PointMark } from './GuideTracker'
import GuideNotesPanel from './GuideNotesPanel'
import { PILL_RADIUS } from '../../agent/components/surface'
import { RESPONSE_STATUS_LABELS, isPointDone, pointStatus, progressLabel, progressOf, readByLine } from '../../utils/guided'
import type { GuideNoteView, GuidePoint, GuideResponseView } from '../../types/gotypes'

/** Dots past this many collapse into "+n", as the rail's other pills do. */
const RAIL_MAX_DOTS = 8

/** The closed panel's rail pill: one mark per agenda point, in order. */
export function GuideRailDots({ points, states }: Pick<GuideResponseView, 'points' | 'states'>) {
  const shown = points.slice(0, RAIL_MAX_DOTS)
  const more = points.length - shown.length
  return (
    <Box component="span" data-testid="guide-rail-dots" sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
      {shown.map((p) => <PointMark key={p.id} status={pointStatus(states, p.id)} size={13} />)}
      {more > 0 && <Box component="span" sx={{ fontSize: 10 }}>+{more}</Box>}
    </Box>
  )
}

export interface GuidePanelProps {
  view: GuideResponseView
  /** Why the person may not change anything here; undefined when they may. */
  disabledReason?: string
  /** Why the person may not change the notes; undefined when they may (notes outlive the conversation). */
  notesDisabledReason?: string
  busy?: boolean
  actionError?: string
  onCoverPoint: (point: GuidePoint) => void
  onSkipPoint: (point: GuidePoint) => void
  onAddNote: (pointId: string | null) => void
  onEditNote: (note: GuideNoteView) => void
  onFinish: () => void
  onOpenAgenda: () => void
}

/**
 * The right-hand panel of a guided conversation: where the person is in the
 * agenda, what was distilled, and Finish now. Drawn in the chat's panel
 * (AgentChat panelSection), so it sits beside the one composer.
 */
const GuidePanel: React.FC<GuidePanelProps> = ({
  view, disabledReason, notesDisabledReason, busy, actionError, onCoverPoint, onSkipPoint, onAddNote, onEditNote, onFinish, onOpenAgenda,
}) => {
  const [confirmFinish, setConfirmFinish] = useState(false)
  const progress = progressOf(view.points, view.states)
  const label = progressLabel(view.points, view.states)
  const open = view.points.filter((p) => !isPointDone(pointStatus(view.states, p.id))).length

  return (
    <Box data-testid="guide-panel" sx={{ backgroundColor: 'background.paper', color: 'text.primary', p: 1.5, display: 'flex', flexDirection: 'column', gap: 2, minHeight: '100%', boxSizing: 'border-box' }}>
      <Box>
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3 }}>{view.guide.title}</Typography>
        <Typography data-testid="guide-progress" sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
          {label} · {RESPONSE_STATUS_LABELS[view.response.status]}
        </Typography>
        <Typography data-testid="guide-read-by" sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
          {readByLine(view.reviewers)}
        </Typography>
        <LinearProgress
          variant="determinate"
          color="info"
          value={progress.total ? (progress.done / progress.total) * 100 : 0}
          aria-label={label}
          sx={{ mt: 1, height: 4, borderRadius: PILL_RADIUS }}
        />
        <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
          <Button size="small" color="info" onClick={onOpenAgenda} sx={{ textTransform: 'none', fontSize: 12, borderRadius: PILL_RADIUS }}>
            Agenda (PDF)
          </Button>
          <DisabledReason reason={disabledReason}>
            <Button size="small" color="info" variant="outlined" onClick={() => setConfirmFinish(true)} disabled={busy} sx={{ textTransform: 'none', fontSize: 12, borderRadius: PILL_RADIUS }}>
              Finish now
            </Button>
          </DisabledReason>
        </Box>
        {actionError && <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert>}
      </Box>
      <GuideTracker points={view.points} states={view.states} disabledReason={disabledReason} onCoverPoint={onCoverPoint} onSkipPoint={onSkipPoint} />
      <GuideNotesPanel points={view.points} notes={view.notes} disabledReason={notesDisabledReason} onAdd={onAddNote} onEdit={onEditNote} />
      <Dialog open={confirmFinish} onClose={() => setConfirmFinish(false)} aria-labelledby="guide-finish-title">
        <DialogTitle id="guide-finish-title">Finish now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {open > 0
              ? `${open} ${open === 1 ? 'point is' : 'points are'} not covered yet. If you finish now, the conversation ends here and your notes so far are kept for the people who read them. You cannot continue afterwards.`
              : 'The conversation ends here and your notes are kept for the people who read them. You cannot continue afterwards.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="info" onClick={() => setConfirmFinish(false)}>Keep going</Button>
          <Button
            variant="contained"
            color="info"
            disableElevation
            disabled={busy}
            onClick={() => { setConfirmFinish(false); onFinish() }}
          >
            Finish now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default GuidePanel
